import React from 'react';
import PropTypes from 'prop-types';
import { TextField, InputAdornment, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useField } from 'formik';

/**
 * MuiTextField - Styled TextField with common props
 *
 * Jakob's Law: Standard text input behavior
 * Law of Proximity: Label, input, and helper text grouped together
 */

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 6,
    transition: 'all 0.2s ease-in-out',
    '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginTop: theme.spacing(0.5),
  },
}));

const CharacterCounter = styled(Typography)(({ theme, error }) => ({
  fontSize: '0.75rem',
  color: error ? theme.palette.error.main : theme.palette.text.secondary,
  textAlign: 'right',
  marginTop: theme.spacing(0.25),
}));

const MuiTextField = ({
  name,
  label,
  placeholder,
  type = 'text',
  variant = 'outlined',
  size = 'medium',
  fullWidth = true,
  required = false,
  disabled = false,
  readOnly = false,
  multiline = false,
  rows = 4,
  maxRows,
  startAdornment,
  endAdornment,
  helperText,
  showCharacterCount = false,
  maxLength,
  autoFocus = false,
  autoComplete,
  inputProps = {},
  InputProps = {},
  InputLabelProps = {},
  FormHelperTextProps = {},
  useFormik = true,
  value: controlledValue,
  onChange: controlledOnChange,
  onBlur: controlledOnBlur,
  error: controlledError,
  touched: controlledTouched,
  sx = {},
  ...props
}) => {
  // Try to use Formik context if useFormik is true and name is provided
  let field = {};
  let meta = {};
  let helpers = {};

  try {
    if (useFormik && name) {
      [field, meta, helpers] = useField(name);
    }
  } catch (error) {
    // Not inside a Formik context, use controlled props
  }

  const isFormikField = useFormik && name && field.name;

  const value = isFormikField ? field.value : controlledValue;
  const onChange = isFormikField ? field.onChange : controlledOnChange;
  const onBlur = isFormikField ? field.onBlur : controlledOnBlur;
  const error = isFormikField ? meta.touched && Boolean(meta.error) : controlledError;
  const errorText = isFormikField ? (meta.touched && meta.error) : (controlledError ? helperText : null);

  const currentLength = typeof value === 'string' ? value.length : 0;
  const isOverLimit = maxLength && currentLength > maxLength;

  const combinedInputProps = {
    ...InputProps,
    readOnly,
    ...(startAdornment && {
      startAdornment: (
        <InputAdornment position="start">{startAdornment}</InputAdornment>
      ),
    }),
    ...(endAdornment && {
      endAdornment: (
        <InputAdornment position="end">{endAdornment}</InputAdornment>
      ),
    }),
  };

  const combinedInputPropsLower = {
    ...inputProps,
    ...(maxLength && { maxLength }),
  };

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <StyledTextField
        name={name}
        label={label}
        placeholder={placeholder}
        type={type}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
        multiline={multiline}
        rows={multiline ? rows : undefined}
        maxRows={multiline ? maxRows : undefined}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        value={value ?? ''}
        onChange={onChange}
        onBlur={onBlur}
        error={error || isOverLimit}
        helperText={errorText || helperText}
        InputProps={combinedInputProps}
        inputProps={combinedInputPropsLower}
        InputLabelProps={{
          shrink: placeholder ? true : undefined,
          ...InputLabelProps,
        }}
        FormHelperTextProps={FormHelperTextProps}
        sx={sx}
        {...props}
      />
      {showCharacterCount && maxLength && (
        <CharacterCounter error={isOverLimit}>
          {currentLength}/{maxLength}
        </CharacterCounter>
      )}
    </Box>
  );
};

MuiTextField.propTypes = {
  /** Field name for Formik */
  name: PropTypes.string,
  /** Field label */
  label: PropTypes.string,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Input type */
  type: PropTypes.oneOf(['text', 'email', 'password', 'number', 'tel', 'url', 'search']),
  /** Visual variant */
  variant: PropTypes.oneOf(['outlined', 'filled', 'standard']),
  /** Size of the input */
  size: PropTypes.oneOf(['small', 'medium']),
  /** Full width input */
  fullWidth: PropTypes.bool,
  /** Mark field as required */
  required: PropTypes.bool,
  /** Disable input */
  disabled: PropTypes.bool,
  /** Make input read-only */
  readOnly: PropTypes.bool,
  /** Enable multiline input */
  multiline: PropTypes.bool,
  /** Number of rows for multiline */
  rows: PropTypes.number,
  /** Maximum rows for multiline */
  maxRows: PropTypes.number,
  /** Start adornment element */
  startAdornment: PropTypes.node,
  /** End adornment element */
  endAdornment: PropTypes.node,
  /** Helper text */
  helperText: PropTypes.node,
  /** Show character counter */
  showCharacterCount: PropTypes.bool,
  /** Maximum character length */
  maxLength: PropTypes.number,
  /** Auto focus on mount */
  autoFocus: PropTypes.bool,
  /** Autocomplete attribute */
  autoComplete: PropTypes.string,
  /** Props for the input element */
  inputProps: PropTypes.object,
  /** Props for the Input component */
  InputProps: PropTypes.object,
  /** Props for the InputLabel component */
  InputLabelProps: PropTypes.object,
  /** Props for the FormHelperText component */
  FormHelperTextProps: PropTypes.object,
  /** Use Formik context for field state */
  useFormik: PropTypes.bool,
  /** Controlled value */
  value: PropTypes.any,
  /** Controlled onChange handler */
  onChange: PropTypes.func,
  /** Controlled onBlur handler */
  onBlur: PropTypes.func,
  /** Controlled error state */
  error: PropTypes.bool,
  /** Controlled touched state */
  touched: PropTypes.bool,
  /** Custom styles */
  sx: PropTypes.object,
};

export default MuiTextField;
